import type { LucideIcon } from "lucide-react";
import {
  ClipboardCheck,
  FileText,
  Home,
  ListChecks,
  Megaphone,
  Settings,
  Sparkles,
  Target,
  Users,
  UsersRound,
} from "lucide-react";

import type { BusinessData } from "@/types/businessData";
import type {
  MembershipAccessLevel,
  MembershipPermissions,
  MembershipRole,
} from "@/types/businessMember";
import { getBusinessVertical } from "@graftus/schema";
import { getVerticalConfig, type VerticalNavConfig } from "@/vertical/vertical-config";
import {
  hasProductAccessWithFallback,
  isAccountExpired,
} from "@/utils/productAccess";

/**
 * Flat groups the nav is organised under (redesign 1c), one per product so the
 * headers read the way the products are sold: RECRUIT, LEADS, COACH. Ungrouped
 * features (e.g. Today) render above every group; grouped ones sit under an
 * uppercase, non-collapsible header so nothing takes two clicks to reach.
 */
export const SIDEBAR_NAV_GROUPS = ["recruit", "leads", "coach"] as const;
export type SidebarNavGroupId = (typeof SIDEBAR_NAV_GROUPS)[number];

/**
 * Product switcher order (GRA-78): Coach is the main product, so it's the
 * default and first entry — Recruit and Leads follow. Distinct from
 * `SIDEBAR_NAV_GROUPS`, which orders the (now single, active-product-only)
 * nav group header and predates the switcher.
 */
export const PRODUCT_SWITCH_ORDER: SidebarNavGroupId[] = [
  "coach",
  "recruit",
  "leads",
];

/**
 * Sections the Settings sub-nav groups its pages under, in display order:
 * the workspace itself, things that only concern the signed-in member, then
 * platform connections and AI configuration last.
 */
export const SETTINGS_NAV_SECTIONS = [
  "workspace",
  "personal",
  "platform",
] as const;
export type SettingsNavSectionId = (typeof SETTINGS_NAV_SECTIONS)[number];

/**
 * Leaf link (no icon in sidebar — icon lives on the parent feature). A child
 * is either a tab of a tabbed page (parent has a `url`; the child whose url
 * equals it is the index tab) or a row of a leaf list (parent has no `url`,
 * e.g. Settings behind the footer gear, or Underlag whose first visible row
 * is the sidebar link).
 */
export type SidebarNavChildItem = {
  /** i18n key in the `nav` namespace. */
  titleKey: string;
  /** Grocery-store label (`nav` key), like the feature-level `dvhTitleKey`. */
  dvhTitleKey?: string;
  /** Grocery-store URL, like the feature-level/child-level `dvhTitleKey`. */
  dvhUrl?: string;
  url: string;
  /**
   * Set by `filterSidebarNavFeatures` when `dvhUrl` swapped in `url` for a
   * DVH business: the pre-swap URL (e.g. `/reports`), so visiting it directly
   * still lights up this row even though the sidebar itself now links to
   * `dvhUrl` (e.g. `/bruttovinst`). Not set on the raw config.
   */
  legacyUrl?: string;
  /** Settings sub-nav section (`nav.settingsSections.*`); Settings children only. */
  section?: SettingsNavSectionId;
  /**
   * On phones this tab renders without the page title and tab strip — its
   * body is a full-screen flow of its own (the M5 pulse rating flow).
   */
  mobileFullScreen?: boolean;
  visible?: (ctx: SidebarNavVisibilityContext) => boolean;
};

export type SidebarNavFeature = {
  id: string;
  /** i18n key in the `nav` namespace. */
  titleKey: string;
  /**
   * Label for grocery stores (`nav` key) when the same page carries another
   * name there, e.g. Reports reads "Bruttovinst". Resolved into `titleKey` by
   * `filterSidebarNavFeatures`, so every nav surface agrees.
   */
  dvhTitleKey?: string;
  /** Renders under the "Mer" heading at the end of the Coach nav. */
  more?: boolean;
  icon: LucideIcon;
  /** Shown beside the feature title (e.g. Beta) — a literal, not translated. */
  titleBadge?: string;
  /** Top-level link only (no children), e.g. Dashboard. */
  url?: string;
  /** Flat group this leaf renders under. Ungrouped features render first. */
  group?: SidebarNavGroupId;
  /**
   * Kept in the config for breadcrumb / page-title lookups but never rendered
   * in the main nav list — Settings now lives behind the footer gear (1c).
   */
  hiddenFromNav?: boolean;
  /**
   * Extra URL prefixes this feature owns beyond `url` and its children, for
   * deep routes that live outside its own prefix (e.g. Teams also owns
   * `/coach/members/:memberId`). Active-state and title lookups treat them as
   * the feature itself.
   */
  subtreeUrls?: string[];
  children?: SidebarNavChildItem[];
  visible?: (ctx: SidebarNavVisibilityContext) => boolean;
};

export type SidebarNavVisibilityContext = {
  businessProfile: BusinessData | null;
  remoteConfig: { showIntelligence: boolean; showPulseCheck?: boolean };
  memberAccessLevel?: MembershipAccessLevel;
  /** Role and report capabilities of the active membership (GRA-96). */
  memberRole?: MembershipRole | null;
  memberPermissions?: MembershipPermissions;
  /**
   * The instant to evaluate time-limited access against. Defaults to the
   * current clock; tests and the route guards pass it explicitly so the
   * sidebar and the guards agree on the expiry boundary.
   */
  now?: number;
};

const nowOf = (ctx: SidebarNavVisibilityContext) => ctx.now ?? Date.now();

/** The nav slot of the business's `VerticalConfig` (GRA-167). */
function navConfigFor(ctx: SidebarNavVisibilityContext): VerticalNavConfig {
  return getVerticalConfig(getBusinessVertical(ctx.businessProfile)).nav;
}

/**
 * Report-only accounts (`accessLevel: "none"`, GRA-96) see no people data:
 * no Today, Coach, Pulse, Teams or Playbook. They get the Reports page only.
 */
export function hasPeopleAccess(ctx: SidebarNavVisibilityContext): boolean {
  return ctx.memberAccessLevel !== "none";
}

/** Recruit, Leads and workspace admin pages need full (not team or none) access. */
function hasFullAccess(ctx: SidebarNavVisibilityContext): boolean {
  return ctx.memberAccessLevel !== "team" && ctx.memberAccessLevel !== "none";
}

/**
 * May open the Reports page: members granted a report capability, and anyone
 * with people access in a grocery store. Sales businesses don't get an empty
 * Reports entry.
 */
export function canOpenReports(ctx: SidebarNavVisibilityContext): boolean {
  const permissions = ctx.memberPermissions ?? {};
  if (permissions.reportUpload === true || permissions.reportView === "store") {
    return true;
  }
  // In a grocery store (GRA-83) reports are the main data source: owners and
  // admins upload, and leaders read the store's numbers.
  return (
    navConfigFor(ctx).reportsOpenToPeopleAccess &&
    (hasPeopleAccess(ctx) ||
      ctx.memberRole === "owner" ||
      ctx.memberRole === "admin")
  );
}

const coachVisible = (ctx: SidebarNavVisibilityContext) =>
  ctx.remoteConfig.showIntelligence &&
  ctx.businessProfile != null &&
  !isAccountLocked(ctx) &&
  hasPeopleAccess(ctx);

/** Owners and admins: the sketch's top roles, who see Data and Behörighet. */
export function isOwnerOrAdmin(ctx: SidebarNavVisibilityContext): boolean {
  return ctx.memberRole === "owner" || ctx.memberRole === "admin";
}

/**
 * Whether this vertical's `dvhTitleKey`/`dvhUrl` overrides should swap in —
 * named for the field, not the vertical, so a third vertical can opt in by
 * setting `reportsLabelKey` to something other than "reports" too.
 */
const usesAlternateNavLabels = (ctx: SidebarNavVisibilityContext) =>
  navConfigFor(ctx).reportsLabelKey !== "reports";

/**
 * True once the business has passed its account-level time limit.
 *
 * An expired account is locked down to the dashboard plus the organization and
 * billing settings pages (GRA-45 §6), so a lapsed customer can still see and
 * act on their invoice. Every other nav entry hides rather than linking to a
 * route that would bounce straight back to the dashboard.
 */
export function isAccountLocked(ctx: SidebarNavVisibilityContext): boolean {
  return isAccountExpired(ctx.businessProfile, nowOf(ctx));
}

/** Flattened entry for breadcrumbs / header titles. `titleKey` is a `nav` i18n key. */
export type SidebarNavItem = {
  titleKey: string;
  url: string;
  icon?: LucideIcon;
};

/**
 * Navigation model for the sidebar: a single flat list of rows, Idag first,
 * then one group per product: `RECRUIT`, `LEADS` and `COACH`. Groups are
 * flat — no collapsible parents — so nothing takes two clicks.
 *
 * The Coach group is the grouped nav of Jonas's platform sketch (Oct 2026):
 * Idag, Agera, Mål, Rutiner, Team, Anser, then Underlag under the "Mer"
 * heading. A group's sub-pages are its `children`, rendered as the segmented
 * tab row under the page title (`ShellSubTabs`): Team carries Team,
 * Medarbetare, Check in, Tester and Roller; Underlag carries Bruttovinst (or
 * Rapporter), Jämförelsedata and Data. Pages without a backend yet point at
 * a "Kommer snart" page (`ComingSoonView`). Existing paths are kept
 * (`/reports` is linked from report-uploader e-mails); `/people` and the
 * placeholders under `/coach/*` are the only new ones.
 *
 * A feature with a `url` is a tabbed page whose own url is the index tab; a
 * feature without one (Underlag, Settings) links to its first visible child.
 * The sidebar row lights for the whole group, children included.
 *
 * `settings` is kept here (marked `hiddenFromNav`) so breadcrumb and page-title
 * lookups keep resolving `/settings/*`, but it renders pinned at the bottom
 * of the sidebar rather than in the main list.
 */
export const sidebarNavFeatures: SidebarNavFeature[] = [
  {
    id: "overview",
    titleKey: "today",
    icon: Home,
    url: "/",
    visible: hasPeopleAccess,
  },
  {
    id: "candidates",
    titleKey: "candidates",
    icon: UsersRound,
    url: "/recruit/candidates",
    group: "recruit",
    visible: (ctx) =>
      ctx.businessProfile != null &&
      hasFullAccess(ctx) &&
      hasProductAccessWithFallback(ctx.businessProfile, "recruit", nowOf(ctx)),
  },
  {
    id: "ads",
    titleKey: "ads",
    icon: Megaphone,
    url: "/recruit/ads",
    group: "recruit",
    visible: (ctx) =>
      ctx.businessProfile != null &&
      hasFullAccess(ctx) &&
      hasProductAccessWithFallback(ctx.businessProfile, "recruit", nowOf(ctx)),
  },
  {
    id: "leads",
    titleKey: "leads",
    icon: Target,
    url: "/leads",
    group: "leads",
    visible: (ctx) =>
      ctx.businessProfile != null &&
      hasFullAccess(ctx) &&
      hasProductAccessWithFallback(ctx.businessProfile, "leads", nowOf(ctx)),
  },
  {
    id: "leadsSettings",
    titleKey: "leadSettings",
    icon: Settings,
    url: "/leads/settings",
    group: "leads",
    visible: (ctx) =>
      ctx.businessProfile != null &&
      hasFullAccess(ctx) &&
      hasProductAccessWithFallback(ctx.businessProfile, "leads", nowOf(ctx)),
  },
  {
    // "Agera": what to do, from the playbook's insights and actions. The id
    // and `nav.playbook` stay for the pages and tests that know the old name.
    id: "playbook",
    titleKey: "act",
    icon: ListChecks,
    url: "/coach/playbook",
    group: "coach",
    visible: coachVisible,
    children: [
      { titleKey: "actTodo", url: "/coach/playbook" },
      { titleKey: "actPlatform", url: "/coach/playbook/platform" },
      { titleKey: "goalsActions", url: "/coach/playbook/objectives" },
    ],
  },
  {
    // "Mål" in a sales business: the KPI page with its trends.
    id: "performance",
    titleKey: "goals",
    icon: Target,
    url: "/coach/performance",
    group: "coach",
    visible: (ctx) => coachVisible(ctx) && navConfigFor(ctx).goalsFeature === "performance",
  },
  {
    // "Mål" in a grocery store: focus goals per department, not built yet.
    id: "goals",
    titleKey: "goals",
    icon: Target,
    url: "/coach/goals",
    group: "coach",
    visible: (ctx) => coachVisible(ctx) && navConfigFor(ctx).goalsFeature === "goals",
  },
  {
    // Store routines (sketch "Rutiner"): no backend yet, placeholder page.
    id: "routines",
    titleKey: "routines",
    icon: ClipboardCheck,
    url: "/coach/routines",
    group: "coach",
    visible: (ctx) => coachVisible(ctx) && navConfigFor(ctx).showRoutines,
    children: [
      { titleKey: "routines", url: "/coach/routines" },
      { titleKey: "routinesToday", url: "/coach/routines/today" },
      { titleKey: "routinesFollowup", url: "/coach/routines/followup" },
    ],
  },
  {
    // "Team": the departments, the people list, the check in, the tests and
    // the roles as one group. The member profile lives under /coach/members
    // and lights this row too.
    id: "teams",
    titleKey: "salesTeam",
    icon: Users,
    url: "/coach/teams",
    subtreeUrls: ["/coach/members"],
    group: "coach",
    visible: coachVisible,
    children: [
      { titleKey: "salesTeam", url: "/coach/teams" },
      { titleKey: "people", url: "/people" },
      { titleKey: "manage", url: "/coach/teams/manage", visible: hasFullAccess },
      {
        titleKey: "checkIn",
        url: "/coach/performance/pulse",
        mobileFullScreen: true,
        visible: ({ remoteConfig }) => remoteConfig.showPulseCheck !== false,
      },
      { titleKey: "tests", url: "/coach/teams/tests" },
      {
        titleKey: "roles",
        url: "/coach/teams/roles",
        visible: hasFullAccess,
      },
    ],
  },
  {
    // The full chat with its thread history. ⌘K and the docked panel are
    // shortcuts into the same chat.
    id: "coach",
    titleKey: "anserChat",
    icon: Sparkles,
    url: "/coach",
    group: "coach",
    visible: coachVisible,
    children: [
      { titleKey: "anserChatTab", url: "/coach" },
      { titleKey: "planera", url: "/coach/planera" },
      { titleKey: "insights", url: "/coach/insights" },
      { titleKey: "questions", url: "/coach/questions" },
    ],
  },
  {
    // "Underlag" under the Mer heading: the data the recommendations rest on.
    // Report uploads and the store's numbers (GRA-82) are gated on report
    // capabilities rather than people access, so a report-only account sees
    // this row and nothing else. In a grocery store the report page is the
    // weekly gross-profit report, so it reads "Bruttovinst".
    id: "reports",
    titleKey: "sources",
    icon: FileText,
    group: "coach",
    more: true,
    visible: (ctx) =>
      ctx.remoteConfig.showIntelligence &&
      ctx.businessProfile != null &&
      !isAccountLocked(ctx) &&
      (canOpenReports(ctx) || coachVisible(ctx)),
    children: [
      {
        titleKey: "reports",
        dvhTitleKey: "grossProfit",
        url: "/reports",
        dvhUrl: "/bruttovinst",
        visible: canOpenReports,
      },
      // Year-over-year comparison (sketch "Jämförelsedata").
      { titleKey: "benchmarks", url: "/coach/benchmarks", visible: coachVisible },
      // Monthly reports and the archive (sketch "Rapporter").
      { titleKey: "pdfReports", url: "/coach/reports", visible: coachVisible },
      // Login and usage data (sketch "Data"): owners and admins, placeholder.
      {
        titleKey: "data",
        url: "/coach/data",
        visible: (ctx) => coachVisible(ctx) && isOwnerOrAdmin(ctx),
      },
      // Roles and department ownership (sketch "Behörighet"): owners and admins.
      {
        titleKey: "access",
        url: "/coach/access",
        visible: (ctx) => coachVisible(ctx) && isOwnerOrAdmin(ctx),
      },
    ],
  },
  {
    id: "settings",
    titleKey: "settings",
    icon: Settings,
    hiddenFromNav: true,
    children: [
      { titleKey: "organization", url: "/settings", section: "workspace" },
      {
        titleKey: "businessProfile",
        url: "/settings/profile",
        section: "workspace",
        visible: (ctx) => !isAccountLocked(ctx),
      },
      {
        titleKey: "users",
        url: "/settings/members",
        section: "workspace",
        visible: (ctx) => hasFullAccess(ctx) && !isAccountLocked(ctx),
      },
      {
        titleKey: "billing",
        url: "/settings/billing",
        section: "workspace",
        visible: hasFullAccess,
      },
      {
        titleKey: "appearance",
        url: "/settings/appearance",
        section: "personal",
        visible: (ctx) => !isAccountLocked(ctx),
      },
      {
        titleKey: "notifications",
        url: "/settings/notifications",
        section: "personal",
        visible: (ctx) => !isAccountLocked(ctx),
      },
      {
        titleKey: "apps",
        url: "/apps",
        section: "platform",
        visible: (ctx) => !isAccountLocked(ctx) && hasPeopleAccess(ctx),
      },
      {
        titleKey: "assistants",
        url: "/settings/assistants",
        section: "platform",
        visible: (ctx) => !isAccountLocked(ctx) && hasPeopleAccess(ctx),
      },
      {
        // Coach's AI/chat behavior config (topic presets, starters, prompts).
        // Used to be its own top-level "Settings" row under the Coach nav
        // group — a second entry point competing with the Company gear for
        // the exact same word. It already lived under the same route layout
        // as the rest of this hub, so it belongs here as a tab, not beside it.
        titleKey: "intelligenceSettings",
        url: "/settings/ai",
        section: "platform",
        visible: coachVisible,
      },
      {
        titleKey: "documents",
        url: "/settings/documents",
        section: "platform",
        visible: coachVisible,
      },
      {
        titleKey: "memory",
        url: "/settings/memory",
        section: "platform",
        visible: coachVisible,
      },
    ],
  },
];

function isFeatureVisible(
  feature: SidebarNavFeature,
  ctx: SidebarNavVisibilityContext,
): boolean {
  if (!feature.visible) return true;
  return feature.visible(ctx);
}

function isChildVisible(
  child: SidebarNavChildItem,
  ctx: SidebarNavVisibilityContext,
): boolean {
  if (!child.visible) return true;
  return child.visible(ctx);
}

export function filterSidebarNavFeatures(
  ctx: SidebarNavVisibilityContext,
): SidebarNavFeature[] {
  const dvh = usesAlternateNavLabels(ctx);
  return sidebarNavFeatures
    .filter((f) => isFeatureVisible(f, ctx))
    .map((f) => {
      const titleKey = dvh && f.dvhTitleKey ? f.dvhTitleKey : f.titleKey;
      if (!f.children?.length) return { ...f, titleKey };
      return {
        ...f,
        titleKey,
        children: f.children
          .filter((c) => isChildVisible(c, ctx))
          .map((c) => ({
            ...c,
            ...(dvh && c.dvhTitleKey ? { titleKey: c.dvhTitleKey } : {}),
            ...(dvh && c.dvhUrl ? { url: c.dvhUrl, legacyUrl: c.url } : {}),
          })),
      };
    })
    // A group whose every sub-page is hidden has nothing to link to.
    .filter((f) => f.url || f.hiddenFromNav || (f.children?.length ?? 0) > 0);
}

/**
 * Whether `pathname` is this nav entry or a deeper route under it (e.g. /ads/1 under /ads).
 */
export function pathMatchesItem(pathname: string, url: string): boolean {
  if (url === "/") return pathname === "/";
  if (pathname === url) return true;
  return pathname.startsWith(`${url}/`);
}

/**
 * Whether `childUrl` should light up given `pathname` and the URLs of its
 * siblings.
 *
 * For an *index-style* item — one whose siblings are nested under it (e.g.
 * the Teams tab `/coach/teams` with siblings `/coach/teams/tests` and
 * `/coach/teams/roles`) — we can't naively prefix-match, or it would light up
 * on every sibling route too. Instead it is active for itself and any deeper
 * route it *owns*: a path that falls under `childUrl` but is not claimed by a
 * more-specific sibling. This keeps the Teams tab highlighted across the
 * drill-down (`/coach/teams/:teamId`) while `/coach/teams/tests` correctly
 * lights "Tests" and not "Teams".
 *
 * For a regular item (no nested siblings) we fall through to a prefix match.
 */
export function isChildItemActive(
  pathname: string,
  childUrl: string,
  siblingUrls: string[],
): boolean {
  const descendantSiblings = siblingUrls.filter(
    (other) => other !== childUrl && other.startsWith(`${childUrl}/`),
  );
  if (descendantSiblings.length > 0) {
    if (!pathMatchesItem(pathname, childUrl)) return false;
    return !descendantSiblings.some((s) => pathMatchesItem(pathname, s));
  }
  return pathMatchesItem(pathname, childUrl);
}

/** Every URL prefix a feature owns: its own link plus any `subtreeUrls`. */
function featureOwnUrls(feature: SidebarNavFeature): string[] {
  return [
    ...(feature.url ? [feature.url] : []),
    ...(feature.subtreeUrls ?? []),
  ];
}

/**
 * The single feature whose row should light for `pathname`: the one with the
 * longest owning prefix, its sub-pages included. Longest-match is what keeps
 * the Anser chat (`/coach`) dark while `/coach/teams` is open, Team lit on
 * `/people` and `/coach/members/:id`, and Underlag lit on `/reports`. `/`
 * matches only itself.
 */
export function getActiveSidebarFeatureId(
  features: SidebarNavFeature[],
  pathname: string,
): string | undefined {
  let best: { id: string; length: number } | undefined;
  for (const f of features) {
    // Settings is behind the footer gear, never a lit row in the main list.
    if (f.hiddenFromNav) continue;
    const urls = [
      ...featureOwnUrls(f),
      ...(f.children?.flatMap((c) => (c.legacyUrl ? [c.url, c.legacyUrl] : [c.url])) ?? []),
    ];
    for (const url of urls) {
      if (!pathMatchesItem(pathname, url)) continue;
      if (!best || url.length > best.length)
        best = { id: f.id, length: url.length };
    }
  }
  return best?.id;
}

/**
 * The sub-page of `feature` that `pathname` is on, for the tab row and the
 * page title. The index tab (same url as the feature) owns every deeper
 * route no sibling claims.
 */
export function getActiveChild(
  feature: SidebarNavFeature,
  pathname: string,
): SidebarNavChildItem | undefined {
  const siblingUrls = feature.children?.map((c) => c.url) ?? [];
  return feature.children?.find(
    (c) =>
      isChildItemActive(pathname, c.url, siblingUrls) ||
      (c.legacyUrl != null && isChildItemActive(pathname, c.legacyUrl, siblingUrls)),
  );
}

/** True if this feature’s own link, a subtree URL, or any child matches the path. */
export function isFeatureSubtreeActive(
  feature: SidebarNavFeature,
  pathname: string,
): boolean {
  if (featureOwnUrls(feature).some((url) => pathMatchesItem(pathname, url)))
    return true;
  const siblingUrls = feature.children?.map((c) => c.url) ?? [];
  return (
    feature.children?.some(
      (c) =>
        isChildItemActive(pathname, c.url, siblingUrls) ||
        (c.legacyUrl != null && isChildItemActive(pathname, c.legacyUrl, siblingUrls)),
    ) ?? false
  );
}

/**
 * All nav items for path title lookup. Subtree URLs title as the feature; the
 * index tab of a tabbed page is skipped so its URL titles as the page. A
 * child with a `dvhUrl` (e.g. Bruttovinst over `/reports`) gets a second
 * entry for that URL, since this list is built once from the raw config and
 * not re-derived per business the way `filterSidebarNavFeatures` is.
 */
export function getAllSidebarNavItems(): SidebarNavItem[] {
  const out: SidebarNavItem[] = [];
  for (const f of sidebarNavFeatures) {
    for (const url of featureOwnUrls(f)) {
      out.push({ titleKey: f.titleKey, url, icon: f.icon });
    }
    for (const c of f.children ?? []) {
      if (c.url !== f.url) out.push({ titleKey: c.titleKey, url: c.url });
      if (c.dvhUrl) out.push({ titleKey: c.dvhTitleKey ?? c.titleKey, url: c.dvhUrl });
    }
  }
  return out;
}

/**
 * Returns [parentTitleKey, childTitleKey] (`nav` namespace) for a pathname by
 * longest URL prefix match. A feature's own url, its subtree URLs and the index
 * tab of a tabbed page give [feature, undefined]; any other child gives
 * [feature, child]. Callers translate the keys.
 */
export function getBreadcrumbTitles(
  pathname: string,
): [string, string | undefined] | undefined {
  let best:
    | { titles: [string, string | undefined]; length: number }
    | undefined;
  const consider = (url: string, titles: [string, string | undefined]) => {
    if (!pathMatchesItem(pathname, url)) return;
    if (!best || url.length > best.length)
      best = { titles, length: url.length };
  };
  for (const f of sidebarNavFeatures) {
    for (const url of featureOwnUrls(f)) consider(url, [f.titleKey, undefined]);
    for (const c of f.children ?? []) {
      consider(c.url, [f.titleKey, c.url === f.url ? undefined : c.titleKey]);
      // A child's `dvhUrl` (e.g. Bruttovinst over `/reports`) titles the same
      // as the child itself, swapped to its grocery-store label.
      if (c.dvhUrl) {
        consider(c.dvhUrl, [f.titleKey, c.dvhUrl === f.url ? undefined : (c.dvhTitleKey ?? c.titleKey)]);
      }
    }
  }
  return best?.titles;
}

/**
 * Resolves the nav item title key (`nav` namespace) for a pathname using
 * longest URL prefix match. Home "/" only matches the item with url "/".
 * Callers translate the returned key.
 */
export function getNavTitleForPathname(
  pathname: string,
  items: SidebarNavItem[] = getAllSidebarNavItems(),
): string | undefined {
  if (pathname === "/") {
    const home = items.find((i) => i.url === "/");
    return home?.titleKey;
  }

  const candidates = items.filter(
    (i) => i.url !== "/" && pathMatchesItem(pathname, i.url),
  );
  if (candidates.length === 0) return undefined;

  candidates.sort((a, b) => b.url.length - a.url.length);
  return candidates[0].titleKey;
}
